import { useCallback, useId, useMemo, useRef, useState, type ReactNode } from "react";
import {
  Modal, Button, Notice, Badge, Card, Icon,
  ObjectPage, ObjectPageSection,
  type BadgeTone, type BeforeNavigateDetail, type SelectedSectionChangeDetail, type StatusBadge,
} from "@conuti-das/prince-ui";
import { Flex, Stack, princeIcon } from "@/ui-prince";
import { marked } from "marked";
import type { ChangelogEntry } from "@/types/changelog";
import type { ChangelogDecision } from "@/hooks/useChangelogReviewState";
import { buildChangelogStory, changelogEntryKey } from "@/lib/changelogStory";
import {
  type DiffTone, PROV_LABEL, PROV_TONE, TYPE_LABEL, TYPE_SYM, sourceDocHref, toneOf,
} from "@/lib/changelogLabels";
import { SourceDocViewer } from "./SourceDocViewer";
import { TicketDraftSection } from "./TicketDraftSection";
import { CommentsSection } from "./CommentsSection";
import "./changelogDialog.css";

marked.setOptions({ gfm: true, breaks: false });

const TONE_BADGE: Record<DiffTone, BadgeTone> = {
  added: "green",
  removed: "red",
  modified: "orange",
  neutral: "gray",
};

const DATE_FMT = new Intl.DateTimeFormat("de-DE", { dateStyle: "medium", timeStyle: "short" });

type Props = {
  entry: ChangelogEntry;
  /** Lauf-ID des Changelogs — Schlüssel für Kommentare und JIRA-Anlage. */
  runId: string;
  decision?: ChangelogDecision;
  onClose: () => void;
  onApprove: () => void;
  onReject: (reason: string) => void;
  onReset: () => void;
  /** Story wurde in JIRA angelegt → Key in den lokalen Review-State. */
  onJiraCreated?: (jiraKey: string) => void;
};

function statusBadge(decision?: ChangelogDecision): StatusBadge {
  if (decision?.jira_key) return { label: `in JIRA: ${decision.jira_key}`, tone: "positive" };
  if (decision?.state === "approved") return { label: "approved", tone: "positive" };
  if (decision?.state === "rejected") return { label: "rejected", tone: "negative" };
  return { label: "offen", tone: "neutral" };
}

/**
 * Changelog-Popup v2: Dialog mit ObjectPage (Story · Diff · Dokumente · Ticket ·
 * Kommentare · Referenzen). Dokumente, Ticket und Kommentare sprechen den Runner
 * an und laden erst, wenn man sie anwählt (die ObjectPage mountet alle Sections).
 */
export function ChangelogDetailDialog({
  entry, runId, decision, onClose, onApprove, onReject, onReset, onJiraCreated,
}: Props) {
  const ids = useId();
  const [rejectMode, setRejectMode] = useState(false);
  const [rejectReason, setRejectReason] = useState(decision?.reason ?? "");
  const [opened, setOpened] = useState<ReadonlySet<string>>(() => new Set());
  const [ticketDirty, setTicketDirty] = useState(false);
  const [commentDirty, setCommentDirty] = useState(false);
  // Dieselben Flags zusätzlich als Ref: Die Sections melden per Effekt, und der
  // setState daraus rendert erst im nächsten Scheduler-Takt. Ein Escape direkt
  // nach dem Tippen käme sonst noch beim alten »nicht dirty« an (im Prod-Bau
  // gemessen: 4 von 10 Läufen). Der Ref steht schon, wenn der Effekt läuft.
  const sectionDirty = useRef({ ticket: false, comment: false });
  const reportTicketDirty = useCallback((d: boolean) => {
    sectionDirty.current.ticket = d;
    setTicketDirty(d);
  }, []);
  const reportCommentDirty = useCallback((d: boolean) => {
    sectionDirty.current.comment = d;
    setCommentDirty(d);
  }, []);

  const entryKey = changelogEntryKey(entry);
  const storyHtml = useMemo(() => marked.parse(buildChangelogStory(entry)) as string, [entry]);
  const idLabel = entry.aend_id ?? entry.doc_diff_id ?? "—";
  const headerTitle = `Änderung · ${idLabel} · ${entry.doctype.toUpperCase()}`;
  const typeText = [TYPE_SYM[entry.change_type], TYPE_LABEL[entry.change_type] ?? entry.change_type]
    .filter(Boolean)
    .join(" ");
  const docHref = sourceDocHref(entry);

  const open = useCallback(
    (id: string) => setOpened((s) => (s.has(id) ? s : new Set(s).add(id))),
    [],
  );
  // Anwählen in der Anker-Leiste (Klick/Tastatur) öffnet jede Section.
  const onBeforeNavigate = useCallback((d: BeforeNavigateDetail) => open(d.sectionId), [open]);
  // Die ObjectPage meldet auch Sections, die man nur in den Blick scrollt. Dokument und
  // Kommentare laden dann mit; der KI-Entwurf (LLM-Aufruf) nur auf ausdrückliche Wahl.
  const onSelectedSectionChange = useCallback(
    (d: SelectedSectionChangeDetail) => {
      if (d.selectedSectionId !== "ticket") open(d.selectedSectionId);
    },
    [open],
  );

  // Ungespeicherter Ticket-/Kommentar-/Begründungstext: weder Klick neben das Fenster
  // noch Escape schließt es; »Schließen« bleibt der ausdrückliche Weg.
  const dirty = ticketDirty || commentDirty || (rejectMode && rejectReason.trim() !== "");
  // Mit angelegter JIRA-Story ohne eigene Bewertung gibt es nichts zurückzusetzen
  // (das Ticket bleibt vermerkt, siehe clearDecision).
  const canReset = decision != null && !(decision.jira_key && decision.state === "approved");

  const headerArea = (
    <Stack style={{ gap: "var(--s-2)" }}>
      <Flex style={{ gap: "var(--s-2)", flexWrap: "wrap" }}>
        <Badge tone={TONE_BADGE[PROV_TONE[entry.provenance] ?? "neutral"]}>
          Herkunft: {PROV_LABEL[entry.provenance] ?? entry.provenance}
        </Badge>
        <Badge tone={TONE_BADGE[toneOf(entry.change_type)]}>{`Typ: ${typeText}`}</Badge>
        {entry.match_confidence != null && (
          <Badge tone="teal">{`Confidence: ${entry.match_confidence.toFixed(2)}`}</Badge>
        )}
        <Badge tone="blue">{`FV ${entry.formatversion}`}</Badge>
        {entry.format && <Badge tone="blue">{entry.format}</Badge>}
        {entry.pruefid && <Badge tone="neutral">{`Prüfi ${entry.pruefid}`}</Badge>}
      </Flex>
      {decision && (
        <Notice tone={decision.state === "approved" ? "positive" : "negative"}>
          {decision.state === "approved" ? "Freigegeben" : "Verworfen"}
          {decision.reviewed_at && ` am ${DATE_FMT.format(new Date(decision.reviewed_at))}`}
          {decision.reason && ` · Grund: „${decision.reason}"`}
          {" "}(lokal in diesem Browser)
        </Notice>
      )}
    </Stack>
  );

  const footer = (
    <Stack className="cl-v2-footer">
      {rejectMode && (
        <Card title="Verwerfen — Begründung">
          <Stack style={{ gap: "var(--s-2)", padding: "var(--s-3)" }}>
            <label className="prn-field-label" htmlFor={`${ids}-reject`}>
              Warum wird dieser Eintrag verworfen?
            </label>
            <textarea
              id={`${ids}-reject`}
              className="prn-input"
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              rows={3}
              placeholder="z.B. nicht relevant für unsere Komponenten, Duplikat, …"
            />
            <Flex className="cl-v2-actions" justifyContent="End">
              <Button variant="plain" onPress={() => setRejectMode(false)}>Doch nicht</Button>
              <Button
                variant="filled"
                isDisabled={!rejectReason.trim()}
                onPress={() => { onReject(rejectReason.trim()); setRejectMode(false); }}
              >
                <Icon name={princeIcon("decline")} size={16} />
                Verwerfen bestätigen
              </Button>
            </Flex>
          </Stack>
        </Card>
      )}
      <Flex className="cl-v2-actions" justifyContent="End">
        {canReset && (
          <Button variant="plain" onPress={onReset}>
            <Icon name={princeIcon("reset")} size={16} />
            Status zurücksetzen
          </Button>
        )}
        <Button variant="plain" onPress={onClose}>Schließen</Button>
        {/* Getönt statt gefüllt: Verwerfen soll nicht aussehen wie die Freigabe daneben. */}
        <Button variant="tinted" onPress={() => setRejectMode(true)}>
          <Icon name={princeIcon("decline")} size={16} />
          Verwerfen
        </Button>
        <Button variant="filled" onPress={onApprove}>
          <Icon name={princeIcon("accept")} size={16} />
          Approve (lokal)
        </Button>
      </Flex>
    </Stack>
  );

  return (
    <Modal
      isOpen
      onOpenChange={(isOpen) => {
        const dirtyNow = sectionDirty.current.ticket || sectionDirty.current.comment
          || (rejectMode && rejectReason.trim() !== "");
        if (!isOpen && !dirtyNow) onClose();
      }}
      aria-label={headerTitle}
      className="cl-v2-dialog"
      isDismissable={!dirty}
    >
      <ObjectPage
        title={headerTitle}
        subtitle={entry.ort || undefined}
        status={statusBadge(decision)}
        headerArea={headerArea}
        hidePinButton
        mode="default"
        onBeforeNavigate={onBeforeNavigate}
        onSelectedSectionChange={onSelectedSectionChange}
        footerArea={footer}
      >
        <ObjectPageSection id="story" titleText="Story">
          <div className="markdown-preview cl-v2-md" dangerouslySetInnerHTML={{ __html: storyHtml }} />
        </ObjectPageSection>

        <ObjectPageSection id="diff" titleText="Diff">
          <Stack className="cl-v2-stack">
            <div>
              <div className="cl-v2-label">Ort</div>
              <div className="cl-v2-text">{entry.ort || "—"}</div>
            </div>
            <div className="cl-v2-diff">
              <div>
                <div className="cl-v2-label">Bisher</div>
                <div className="cl-v2-text cl-v2-mono cl-v2-before">
                  {entry.before || <span className="cl-v2-empty">leer</span>}
                </div>
              </div>
              <div>
                <div className="cl-v2-label">Neu</div>
                <div className="cl-v2-text cl-v2-mono cl-v2-after">
                  {entry.after || <span className="cl-v2-empty">leer</span>}
                </div>
              </div>
            </div>
            <div>
              <div className="cl-v2-label">Begründung</div>
              <div className="cl-v2-text">{entry.reason || "—"}</div>
            </div>
          </Stack>
        </ObjectPageSection>

        <ObjectPageSection id="docs" titleText="Dokumente">
          {opened.has("docs") ? (
            <SourceDocViewer entry={entry} />
          ) : (
            <LazyPlaceholder action="Dokument laden" onPress={() => open("docs")}>
              Quelldokument alt/neu aus der Wissenssammlung, mit Sprung zur geänderten Stelle.
            </LazyPlaceholder>
          )}
        </ObjectPageSection>

        <ObjectPageSection id="ticket" titleText="Ticket">
          {opened.has("ticket") ? (
            <TicketDraftSection
              entry={entry}
              runId={runId}
              entryKey={entryKey}
              jiraKey={decision?.jira_key}
              onJiraCreated={(k) => onJiraCreated?.(k)}
              onDirtyChange={reportTicketDirty}
            />
          ) : (
            <LazyPlaceholder action="KI-Entwurf erzeugen" onPress={() => open("ticket")}>
              {decision?.jira_key
                ? <>Schon in JIRA angelegt: <strong>{decision.jira_key}</strong>.</>
                : "Die KI formuliert aus der Änderung eine User Story vor; danach bearbeiten und in JIRA anlegen."}
            </LazyPlaceholder>
          )}
        </ObjectPageSection>

        <ObjectPageSection id="comments" titleText="Kommentare">
          {opened.has("comments") ? (
            <CommentsSection runId={runId} entryKey={entryKey} onDirtyChange={reportCommentDirty} />
          ) : (
            <LazyPlaceholder action="Kommentare laden" onPress={() => open("comments")}>
              Geteilte Kommentare der Kolleginnen und Kollegen zu dieser Änderung.
            </LazyPlaceholder>
          )}
        </ObjectPageSection>

        <ObjectPageSection id="refs" titleText="Referenzen">
          <dl className="cl-v2-kv">
            <KV label="Herkunft" value={PROV_LABEL[entry.provenance] ?? entry.provenance} />
            <KV label="Doctype" value={entry.doctype.toUpperCase()} />
            <KV label="Format" value={entry.format} />
            <KV label="Prüfidentifikator" value={entry.pruefid} mono />
            <KV label="Formatversion" value={entry.formatversion} mono />
            <KV label="Version" value={entry.version} />
            <KV label="Status" value={entry.status} />
            <KV label="aend_id" value={entry.aend_id} mono />
            <KV label="Doku-Diff-ID" value={entry.doc_diff_id} mono />
            <KV label="Ort" value={entry.ort} />
            <KV
              label="Quelldatei"
              mono
              value={entry.source_file}
              render={docHref ? (v) => <a href={docHref} target="_blank" rel="noreferrer">{v}</a> : undefined}
            />
          </dl>
        </ObjectPageSection>
      </ObjectPage>
    </Modal>
  );
}

function LazyPlaceholder({ action, onPress, children }: { action: string; onPress: () => void; children: ReactNode }) {
  return (
    <Flex className="cl-v2-lazy" alignItems="Center" justifyContent="SpaceBetween">
      <span className="muted">{children}</span>
      <Button variant="tinted" onPress={onPress}>{action}</Button>
    </Flex>
  );
}

function KV({
  label, value, mono, render,
}: {
  label: string;
  value: string | null | undefined;
  mono?: boolean;
  render?: (v: string) => ReactNode;
}) {
  const v = (value ?? "").trim();
  return (
    <>
      <dt className="cl-v2-kv-key">{label}</dt>
      <dd className={mono ? "cl-v2-kv-val cl-v2-mono" : "cl-v2-kv-val"}>
        {v ? (render ? render(v) : v) : "—"}
      </dd>
    </>
  );
}
